import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import {
    Button,
    HeaderFilter,
    Loading,
    PanelMenu,
    PanelMenuItem,
    RoleType,
    useDebounced,
    useTitle,
} from "modules/common";
import {
    RecordCategoryModal,
    RecordDetailsModal,
    RecordsTable,
    useGetRecordCategories,
    useGetRecords,
} from "modules/records";
import { RecordsFilters } from "modules/records/components/RecordsFilters";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { SortingRule } from "react-table";
import { AppRoutes, RouteParams } from "routing";
import { Record, RecordCategory } from "services/records";
import { RootState } from "store";
import { AccessibilityContext } from "./AccessibilityContext";

const INPUT_DEBOUNCE_TIME = 500;

export const Records: React.FC = () => {
    useTitle("Evidencije - eSavjetnik");

    const [menuItem, setMenuItem] = useState<PanelMenuItem>();
    const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [recordId, setRecordId] = useState<string | null>(null);

    const location = useLocation();
    const history = useHistory();

    const [sortBy, setSortBy] = useState<SortingRule<object>>();
    const [professionalAreaFilter, setProfessionalAreaFilter] = useState<string[]>([]);
    const [productionTypeFilter, setProductionTypeFilter] = useState<string[]>([]);
    const [deadlineFilter, setDeadlineFilter] = useState<number[]>([]);
    const [selectedRecords, setSelectedRecords] = useState<Record[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<RecordCategory>();
    const setCategory = (categoryGuid: string) => {
        setSelectedCategory(recordCategoriesData?.find(rc => rc.guid === categoryGuid));
        setMenuItem(menuItems?.find(mi => mi.link === categoryGuid));
    };
    const [latestSelectedCategory, setLatestSelectedCategory] = useState<RecordCategory>();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const search = useDebounced(searchTerm, INPUT_DEBOUNCE_TIME);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_OPTION);

    const { profile } = useSelector((state: RootState) => state.profile);

    const { data: recordCategoriesData, isFetching: isRecordCategoriesLoading } =
        useGetRecordCategories();

    const menuItems = useMemo(
        () =>
            recordCategoriesData?.map(category => ({
                link: category.guid,
                label: category.name,
            })),
        [recordCategoriesData]
    );

    const { data: recordsData, isFetching: isRecordsLoading } = useGetRecords({
        search,
        productionTypeGuids: productionTypeFilter,
        professionalAreaGuids: professionalAreaFilter,
        deadlineFilters: deadlineFilter,
        page: currentPage,
        pageSize: parseInt(pageSize.value),
        recordCategoryGuid: menuItem?.link,
    });

    useEffect(() => {
        if (menuItems?.length) {
            setMenuItem(menuItems[0]);
            setSelectedCategory(recordCategoriesData?.[0]);
        }
    }, [menuItems, recordCategoriesData]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const recordIdFromURL = params.get(RouteParams.RecordId);
        if (!recordIdFromURL) return;

        setRecordId(recordIdFromURL);
        setIsRecordModalOpen(true);
    }, [location.search]);

    const isSuperAdmin = profile?.roleType?.value === RoleType.SuperAdmin;

    const handleModalClose = () => {
        setIsCategoryModalOpen(false);
        setSelectedCategory(latestSelectedCategory);
    };

    const { lowVision } = useContext(AccessibilityContext);

    return (
        <>
            <div className="records-container">
                <div className="panel-menu-container">
                    {isRecordCategoriesLoading ? (
                        <Loading />
                    ) : (
                        <div className="f-c g-24">
                            <PanelMenu
                                isInput
                                input={searchTerm}
                                setInput={setSearchTerm}
                                items={menuItems}
                                selectedItem={menuItem}
                                isRightIcon
                                onItemClicked={item => {
                                    setMenuItem(item);
                                    setSelectedCategory(
                                        recordCategoriesData?.find(
                                            category => category.guid === item.link
                                        )
                                    );
                                    setSelectedRecords([]);
                                    setCurrentPage(1);
                                }}
                            />

                            {isSuperAdmin && (
                                <div className="record-categories-menu-buttons">
                                    <Button
                                        size="small"
                                        variant="primary-ghost"
                                        lowVision={lowVision}
                                        disabled={isRecordCategoriesLoading || !selectedCategory}
                                        onClick={() => {
                                            setLatestSelectedCategory(selectedCategory);
                                            setIsCategoryModalOpen(Boolean(selectedCategory));
                                        }}
                                    >
                                        UREDI
                                    </Button>

                                    <Button
                                        size="small"
                                        variant="primary"
                                        lowVision={lowVision}
                                        disabled={isRecordCategoriesLoading}
                                        onClick={() => {
                                            setLatestSelectedCategory(selectedCategory);
                                            setSelectedCategory(undefined);
                                            setIsCategoryModalOpen(true);
                                        }}
                                    >
                                        NOVA KATEGORIJA
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="f-c g-24 f-1">
                    <div className="f-r g-12">
                        <HeaderFilter name="records-filter" title="EVIDENCIJE">
                            <RecordsFilters
                                professionalAreaFilter={professionalAreaFilter}
                                productionTypeFilter={productionTypeFilter}
                                deadlineFilter={deadlineFilter}
                                setProfessionalAreaFilter={setProfessionalAreaFilter}
                                setProductionTypeFilter={setProductionTypeFilter}
                                setDeadlineFilter={setDeadlineFilter}
                                setCurrentPage={setCurrentPage}
                            />
                        </HeaderFilter>

                        {isSuperAdmin && (
                            <Button
                                size="small"
                                variant="primary"
                                lowVision={lowVision}
                                disabled={isRecordCategoriesLoading || isRecordsLoading}
                                onClick={() => {
                                    setSelectedRecords([]);
                                    setRecordId(null);
                                    setIsRecordModalOpen(true);
                                }}
                            >
                                DODAJ
                            </Button>
                        )}
                    </div>

                    {isRecordsLoading && !menuItem ? (
                        <Loading />
                    ) : (
                        <RecordsTable
                            isLoading={isRecordsLoading}
                            records={recordsData}
                            setSortBy={setSortBy}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            selectedRows={selectedRecords}
                            setSelectedRows={setSelectedRecords}
                        />
                    )}
                </div>
            </div>

            {isRecordModalOpen && (
                <RecordDetailsModal
                    isOpen={isRecordModalOpen}
                    handleClose={() => {
                        if (recordId) {
                            history.push(AppRoutes.Records);
                            setRecordId(null);
                        }
                        setIsRecordModalOpen(false);
                    }}
                    records={selectedRecords}
                    recordId={recordId}
                    chosenPage={currentPage}
                    onPageChange={setCurrentPage}
                    setCategory={setCategory}
                />
            )}

            <RecordCategoryModal
                isOpen={isCategoryModalOpen}
                handleClose={handleModalClose}
                recordCategory={selectedCategory}
                hasRecords={Boolean(recordsData?.items.length)}
            />
        </>
    );
};
